// containers/Scanner_Form/Scanner_Form.jsx
//
// Visually matches TradingBotDashboard: standard MUI dark theme, Roboto font,
// blue primary accent, Paper variant="outlined" panels, rounded buttons,
// descriptive helper text. State, validation, payload, and API behavior preserved.

import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  Box, Typography, Paper, Button, Tabs, Tab, Card, CardContent,
  TextField, Stepper, Step, StepLabel, Grid, Chip, Stack,
  IconButton, Tooltip, Collapse, Snackbar, Alert,
  Slider, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, Divider,
} from '@mui/material';
import {
  AddCircleOutline, Settings, PlayArrow, Pause,
  Delete, Edit, KeyboardArrowDown, KeyboardArrowUp, Bolt,
  WarningAmber, AccountBalance, TrendingUp, ShowChart,
  AttachMoney, Timeline, CheckCircle, RadioButtonUnchecked,
} from '@mui/icons-material';

import Toolbar from '../../components/Toolbar/Toolbar';
import Logout from '../../containers/Login/Logout';
import scannerApi from '../../services/scannerApi';
import ScannerTriggerBuilder from '../../components/ScannerTriggerBuilder';
import UniverseSelector from '../../components/UniverseSelector';
import LiveTriggerFeed from '../../components/LiveTriggerFeed';
import StrategyBuilder from '../../components/conditionBuilder';
import exchangeApiService from '../../services/exchangeApiService';
import { exchangeDisplayName, exchangeSubtitle } from '../../config/exchangeConfig';
import ExchangeOptionRow from '../../components/ExchangeOptionRow';
import {
  buildLogicFromConditions,
  extractIndicatorsFromConditions,
} from '../../components/strategyUtils';

// Phase B
import { AssetClassChip, getAssetClassFromExchange } from '../../components/AssetClassChip';
import { StockUniversePicker } from '../../components/StockUniversePicker';

const STEPS = ['Basics', 'Trigger', 'Strategy', 'Safety'];

// Funding asset options per asset class.
// Stock equity is USD-only (Alpaca constraint).
// Crypto list is the common stablecoins + majors; balance figures are filled
// in by useFundingBalances() once a balance endpoint is wired up.
const FUNDING_ASSETS_CRYPTO = ['USDT', 'USDC', 'FDUSD', 'BUSD', 'BTC', 'ETH'];
const FUNDING_ASSETS_STOCK = ['USD'];

// ── Matte/steel-blue theme (shared design system w/ Portfolio & AutoTrader) ──
const AT_SANS = '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const AT_MONO = '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, monospace';
const AT = {
  bg: '#0a0c11', card: '#0f1218', inner: '#0b0e15', tile: '#070a0f',
  bright: '#f2f6ff', text: '#e8edf7', body: '#9aa6bd', muted: '#6f7c96', muted2: '#7b88a2', faint: '#5e6a82',
  accent: '#4d8fc7', accent2: '#3f6fb0', positive: '#46b88a', negative: '#d97b72', amber: '#d9a441',
  hairline: 'rgba(255,255,255,.06)',
};
const AT_THEME = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: AT.accent }, success: { main: AT.positive }, error: { main: AT.negative }, warning: { main: AT.amber }, info: { main: AT.accent },
    background: { default: AT.bg, paper: AT.card }, divider: AT.hairline,
  },
  shape: { borderRadius: 12 },
  typography: { fontFamily: AT_SANS },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#0b0e15', borderRadius: 9,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.1)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.2)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4d8fc7', borderWidth: 1 },
        },
        input: { color: '#f2f6ff' },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: '#7b88a2', '&.Mui-focused': { color: '#4d8fc7' } } } },
    MuiPaper: { styleOverrides: { outlined: { borderColor: 'rgba(255,255,255,.06)' } } },
    MuiSlider: { styleOverrides: { root: { color: '#4d8fc7' } } },
    MuiStepConnector: { styleOverrides: { line: { borderColor: 'rgba(255,255,255,.12)' } } },
  },
});

function getFundingAssetsForClass(assetClass) {
  return assetClass === 'stock_equity' ? FUNDING_ASSETS_STOCK : FUNDING_ASSETS_CRYPTO;
}

function defaultFundingAssetForClass(assetClass) {
  return assetClass === 'stock_equity' ? 'USD' : 'USDT';
}

/* ════════════════════════════════════════════════════════════════════════
   PAGE
   ════════════════════════════════════════════════════════════════════════ */

export default function ScannerDashboard() {
  const [activeTab, setActiveTab] = useState('list');
  const [profiles, setProfiles] = useState([]);
  const [spawnedByProfile, setSpawnedByProfile] = useState({});
  const [expandedProfile, setExpandedProfile] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [isCreating, setIsCreating] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(makeDraft());
  const [userExchanges, setUserExchanges] = useState([]);

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    exchangeApiService.list()
      .then(setUserExchanges)
      .catch(err => showToast('error', `Failed to load exchanges: ${err.message}`));
  }, []);

  async function refresh() {
    try {
      const p = await scannerApi.scannerProfiles.list();
      setProfiles(p || []);
      const spawnEntries = await Promise.all((p || []).map(async (prof) => {
        try {
          const bots = await scannerApi.scannerProfiles.spawned(prof.id, 50);
          return [prof.id, bots];
        } catch { return [prof.id, []]; }
      }));
      setSpawnedByProfile(Object.fromEntries(spawnEntries));
    } catch (err) {
      showToast('error', `Failed to load: ${err.message}`);
    }
  }

  function showToast(severity, message) { setToast({ open: true, severity, message }); }
  function startCreate() { setDraft(makeDraft()); setEditingId(null); setActiveStep(0); setIsCreating(true); setActiveTab('create'); }
  function startEdit(profile) { setDraft(profileToDraft(profile)); setEditingId(profile.id); setActiveStep(0); setIsCreating(true); setActiveTab('create'); }
  function cancelCreate() { setIsCreating(false); setEditingId(null); setDraft(makeDraft()); setActiveTab('list'); }

  async function handleSave(enabled = true) {
    const errors = validateDraft(draft);
    if (errors.length) { showToast('error', errors[0]); return; }
    let payload;
    try { payload = draftToPayload(draft, enabled); }
    catch (err) { showToast('error', err.message); return; }

    try {
      if (editingId) {
        await scannerApi.scannerProfiles.update(editingId, payload);
        showToast('success', 'Scanner updated');
      } else {
        await scannerApi.scannerProfiles.create(payload);
        showToast('success', enabled ? 'Scanner active' : 'Scanner saved (paused)');
      }
      await refresh();
      cancelCreate();
    } catch (err) {
      showToast('error', err?.response?.data?.error || err.message);
    }
  }

  async function handleToggle(profile) {
    try { await scannerApi.scannerProfiles.toggle(profile.id); await refresh(); }
    catch (err) { showToast('error', err.message); }
  }

  async function handleDelete(id) {
    try {
      await scannerApi.scannerProfiles.remove(id);
      setConfirmDeleteId(null);
      showToast('success', 'Scanner deleted');
      await refresh();
    } catch (err) { showToast('error', err.message); }
  }

  return (
    <ThemeProvider theme={AT_THEME}>
    <Box sx={{ minHeight: '100vh', bgcolor: AT.bg, color: AT.text, fontFamily: AT_SANS, '@keyframes clp': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 1 } } }}>
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(t => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Toolbar button={<Logout />} />

      <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 3 }}>
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{ color: 'text.primary', mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <Bolt fontSize="large" />
          Multi-Symbol Scanners
          <Chip
            size="small"
            label="Spot Only"
            variant="outlined"
            sx={{ ml: 1, fontWeight: 600 }}
          />
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ mb: 3 }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="My Scanners" value="list" icon={<Settings />} />
          <Tab label="Create Scanner" value="create" icon={<AddCircleOutline />} onClick={() => !isCreating && startCreate()} />
          <Tab label="Live Feed" value="feed" icon={<Bolt />} />
        </Tabs>

        {activeTab === 'list' && (
          <ProfileList
            profiles={profiles}
            spawnedByProfile={spawnedByProfile}
            expandedProfile={expandedProfile}
            onExpand={setExpandedProfile}
            onCreate={startCreate}
            onEdit={startEdit}
            onToggle={handleToggle}
            onDelete={(id) => setConfirmDeleteId(id)}
          />
        )}

        {activeTab === 'create' && isCreating && (
          <CreateForm
            draft={draft} setDraft={setDraft}
            activeStep={activeStep} setActiveStep={setActiveStep}
            editing={Boolean(editingId)}
            onCancel={cancelCreate} onSave={handleSave}
            userExchanges={userExchanges}
          />
        )}

        {activeTab === 'create' && !isCreating && (
          <Paper elevation={0} sx={{
            p: 4, textAlign: 'center',
            border: '1px solid #e8edf7', borderRadius: 2,
          }}>
            <Typography variant="h6" gutterBottom>No active draft</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Start a new scanner to define a universe and trigger
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={startCreate}
              sx={{ bgcolor: 'grey.800', '&:hover': { bgcolor: 'grey.700' } }}
            >
              Create Scanner
            </Button>
          </Paper>
        )}

        {activeTab === 'feed' && (
          <LiveTriggerFeed profiles={profiles} spawnedBots={spawnedByProfile} />
        )}
      </Box>

      <Dialog
        open={Boolean(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs" fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber color="warning" />
          Delete scanner?
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            This will stop scanning and delete the profile permanently. Bots already
            spawned will continue running until they exit normally.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Delete />}
            onClick={() => handleDelete(confirmDeleteId)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ThemeProvider>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   PROFILE LIST + CARD
   ════════════════════════════════════════════════════════════════════════ */

function ProfileList({ profiles, spawnedByProfile, expandedProfile, onExpand, onCreate, onEdit, onToggle, onDelete }) {
  if (profiles.length === 0) {
    return (
      <>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutline />}
            onClick={onCreate}
            sx={{ bgcolor: 'grey.800', '&:hover': { bgcolor: 'grey.700' } }}
          >
            Create New Scanner
          </Button>
        </Stack>
        <Paper elevation={0} sx={{
          p: 4, textAlign: 'center',
          border: '1px solid #e8edf7', borderRadius: 2,
        }}>
          <Typography variant="h6" gutterBottom>No scanners created yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Get started by creating a scanner that watches a universe and auto-spawns bots when conditions trigger
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddCircleOutline />}
            onClick={onCreate}
            sx={{ bgcolor: 'grey.800', '&:hover': { bgcolor: 'grey.700' } }}
          >
            Create First Scanner
          </Button>
        </Paper>
      </>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddCircleOutline />}
          onClick={onCreate}
          sx={{ bgcolor: 'grey.800', '&:hover': { bgcolor: 'grey.700' } }}
        >
          Create New Scanner
        </Button>
      </Stack>

      <Stack spacing={2}>
        {profiles.map(p => (
          <ProfileCard
            key={p.id}
            profile={p}
            spawnedBots={spawnedByProfile[p.id] || []}
            expanded={expandedProfile === p.id}
            onExpand={() => onExpand(expandedProfile === p.id ? null : p.id)}
            onEdit={() => onEdit(p)}
            onToggle={() => onToggle(p)}
            onDelete={() => onDelete(p.id)}
          />
        ))}
      </Stack>
    </>
  );
}


function ProfileCard({ profile, spawnedBots, expanded, onExpand, onEdit, onToggle, onDelete }) {
  const scannerCfg = useMemo(() => extractScannerCfg(profile), [profile]);
  const tradeInfo = useMemo(() => parseTradeInfo(profile.trade_info), [profile.trade_info]);
  const triggerSummary = useMemo(() => summarizeTrigger(scannerCfg.trigger), [scannerCfg.trigger]);
  const universeSummary = useMemo(() => summarizeUniverse(scannerCfg.universe), [scannerCfg.universe]);
  const enabled = (profile.status === 'scanner_profile') && !scannerCfg.paused;
  const isFutures = tradeInfo.marketType === 'futures';

  return (
    <Card sx={{
      borderRadius: 2,
      border: '1px solid #e8edf7',
      '&:hover': { boxShadow: 2 },
    }}>
      <CardContent sx={{ p: '16px !important' }}>
        <Box
          onClick={onExpand}
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton size="small">
              {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>{profile.name}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label="Scanner Profile"
                  color="primary"
                  variant="outlined"
                  icon={<Bolt sx={{ fontSize: 14 }} />}
                />
                {!enabled && (
                  <Chip
                    size="small"
                    label="Paused"
                    color="warning"
                    icon={<Pause sx={{ fontSize: 14 }} />}
                  />
                )}
                {/* Phase B — show asset class for stock-equity scanners, otherwise the existing spot/futures chip */}
                {tradeInfo.asset_class === 'stock_equity' ? (
                  <Chip
                    size="small"
                    label={`Stocks${profile.short_open_logic ? ' • Long+Short' : ''}`}
                    color="info"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    size="small"
                    label={isFutures ? `Futures ${tradeInfo.futures?.leverage || 1}×` : 'Spot'}
                    color={isFutures ? 'warning' : 'default'}
                    variant="outlined"
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  {universeSummary} • {triggerSummary}
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${spawnedBots.length} spawned`}
              size="small"
              variant="outlined"
            />
            <Tooltip title={enabled ? 'Pause Scanner' : 'Activate Scanner'}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                color={enabled ? 'warning' : 'success'}
              >
                {enabled ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 3, pl: 2, pr: 1 }}>
            <Grid container spacing={3}>
              {/* TRIGGER */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Bolt color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>Trigger</Typography>
                  </Box>
                  <Stack spacing={1.5}>
                    <KV label="Type" value={scannerCfg.trigger?._preset?.kind || scannerCfg.trigger?.type || 'expression'} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Expression</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: 'background.default' }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-word' }}>
                          {scannerCfg.trigger?.expression || triggerSummary}
                        </Typography>
                      </Paper>
                    </Box>
                    <Stack direction="row" spacing={3}>
                      <KV label="Cooldown" value={fmtSeconds(scannerCfg.cooldown_seconds)} sub="per symbol" />
                      <KV label="Interval" value={scannerCfg.primary_interval || tradeInfo.timeframe || '—'} />
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>

              {/* UNIVERSE */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ShowChart color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>Universe</Typography>
                  </Box>
                  <Stack spacing={1.5}>
                    <KV label="Type" value={scannerCfg.universe?.type || '—'} />
                    {scannerCfg.universe?.type === 'top_volume' && (
                      <Stack direction="row" spacing={3}>
                        <KV label="Limit" value={scannerCfg.universe?.limit || 100} />
                        <KV label="Quote" value={scannerCfg.universe?.quote || 'USDT'} />
                        <KV label="Min Volume" value={fmtMoneyShort(scannerCfg.universe?.min_volume) || '0'} />
                      </Stack>
                    )}
                    {scannerCfg.universe?.type === 'list' && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Symbols ({(scannerCfg.universe?.symbols || []).length})
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: 'background.default', maxHeight: 80, overflow: 'auto' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.6 }}>
                            {(scannerCfg.universe?.symbols || []).join(', ') || '—'}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Grid>

              {/* POSITION SIZING */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AttachMoney color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>Position Sizing</Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <KV
                        label={isFutures ? 'Margin' : 'Quote'}
                        value={fmtMoneyShort(tradeInfo.totalInvestment ?? tradeInfo.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <KV label="Market" value={isFutures ? 'Futures' : 'Spot'} />
                    </Grid>
                    <Grid item xs={6}>
                      <KV
                        label="Funding Asset"
                        value={tradeInfo.funding_asset || profile.funding_asset || '—'}
                        sub="balance source"
                      />
                    </Grid>
                    {isFutures && (
                      <>
                        <Grid item xs={6}>
                          <KV label="Leverage" value={`${tradeInfo.futures?.leverage || 1}×`} />
                        </Grid>
                        <Grid item xs={6}>
                          <KV
                            label="Margin Mode"
                            value={(tradeInfo.futures?.marginType || tradeInfo.futures?.marginMode || 'cross').toLowerCase()}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <KV
                            label="Position Mode"
                            value={tradeInfo.futures?.positionSide === 'hedge' ? 'Hedge' : 'One-Way'}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {/* RISK + LIMITS */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <WarningAmber color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>Risk &amp; Limits</Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}><KV label="Take Profit" value={tradeInfo.profitStop || '—'} /></Grid>
                    <Grid item xs={6}><KV label="Stop Loss" value={tradeInfo.lossStop || '—'} /></Grid>
                    <Grid item xs={6}><KV label="Trailing" value={tradeInfo.trailingStop ? `${tradeInfo.trailingStop}%` : '—'} /></Grid>
                    <Grid item xs={6}><KV label="Max Lifetime" value={scannerCfg.max_lifetime_seconds ? fmtSeconds(scannerCfg.max_lifetime_seconds) : 'None'} /></Grid>
                    <Grid item xs={6}><KV label="Max Concurrent" value={scannerCfg.max_concurrent_spawned || '∞'} /></Grid>
                    <Grid item xs={6}>
                      <KV
                        label="Capital Cap"
                        value={scannerCfg.total_capital_cap ? `$${Number(scannerCfg.total_capital_cap).toLocaleString()}` : 'Unlimited'}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* SPAWNED */}
              {spawnedBots.length > 0 && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{
                      px: 2.5, py: 1.5,
                      bgcolor: 'background.default',
                      borderBottom: '1px solid', borderColor: 'divider',
                      display: 'flex', alignItems: 'center', gap: 1,
                    }}>
                      <Timeline color="primary" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={600}>Recent Spawns</Typography>
                      <Chip size="small" variant="outlined" label={`${spawnedBots.length} total`} sx={{ ml: 'auto' }} />
                    </Box>
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {spawnedBots.slice(0, 10).map(b => (
                        <Box
                          key={b.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '160px 120px 1fr 100px',
                            gap: 2, alignItems: 'center', px: 2.5, py: 1,
                            borderBottom: '1px solid', borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {b.createdAt ? new Date(b.createdAt).toLocaleString() : (b.created_at ? new Date(b.created_at).toLocaleString() : '—')}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {(() => { try { return parseTradeInfo(b.trade_info)?.symbol || '—'; } catch { return '—'; } })()}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {b.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={b.status || 'running'}
                            color={b.status === 'terminated' ? 'default' : 'success'}
                            variant="outlined"
                          />
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3, mb: 1 }}>
              <Button variant="outlined" size="small" startIcon={<Edit />} onClick={onEdit}>
                Edit
              </Button>
              <Button variant="outlined" size="small" color="error" startIcon={<Delete />} onClick={onDelete}>
                Delete
              </Button>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   CREATE FORM + STEPS
   ════════════════════════════════════════════════════════════════════════ */

// Persistent right rail for the scanner create flow — mirrors the AutoTrader
// glass-cockpit (§8a): identity, scan scope, readiness gates, spawn strategy, risk.
function SRailRow({ k, v, c }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
      <Typography sx={{ fontSize: '12px', color: AT.muted }}>{k}</Typography>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: c || AT.text, fontFamily: AT_MONO, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{v}</Typography>
    </Box>
  );
}

function SRailSection({ label, children, mb = 2.25 }) {
  return (
    <Box sx={{ mb }}>
      <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '9.5px', letterSpacing: '.12em', color: AT.muted2, display: 'block', mb: '8px' }}>{label}</Box>
      {children}
    </Box>
  );
}

function ScannerRail({ draft, exchangeLabel, readiness, onJump }) {
  const size = Number(draft.position_size) || 0;
  const tp = Number(draft.profit_stop_pct) || 0, sl = Number(draft.loss_stop_pct) || 0;
  const rr = sl > 0 ? tp / sl : 0;
  const band = rr >= 2 ? { label: 'Favorable', c: AT.positive } : rr >= 1 ? { label: 'Balanced', c: AT.accent } : { label: 'Aggressive', c: AT.negative };
  const uni = summarizeUniverse(draft.universe_filter);
  const trig = summarizeTrigger(draft.trigger);
  return (
    <Box sx={{ bgcolor: AT.card, border: `1px solid ${AT.hairline}`, borderRadius: '14px', p: '18px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '4px' }}>
        <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '9.5px', letterSpacing: '.14em', color: AT.accent }}>SCANNER</Box>
        <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '9px', fontWeight: 700, letterSpacing: '.08em', color: AT.accent, background: 'rgba(77,143,199,.12)', border: '1px solid rgba(77,143,199,.34)', px: '7px', py: '2px', borderRadius: '5px' }}>AUTO-SPAWN</Box>
      </Box>
      <Typography sx={{ fontSize: '17px', fontWeight: 700, color: AT.bright, mb: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{draft.name?.trim() || 'Untitled scanner'}</Typography>
      <Typography sx={{ fontSize: '12px', color: AT.body, mb: '16px' }}>{exchangeLabel || '—'} · {draft.primary_interval || '—'}</Typography>

      <SRailSection label="SCAN SCOPE">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <SRailRow k="Universe" v={uni || '—'} />
          <SRailRow k="Trigger" v={trig || '—'} />
          <SRailRow k="Cooldown" v={`${Math.round((Number(draft.cooldown_seconds) || 0) / 60)}m`} />
        </Box>
      </SRailSection>

      <SRailSection label={`READINESS · ${readiness.doneCount}/${readiness.total}`}>
        <Box sx={{ height: 6, borderRadius: '4px', background: 'rgba(255,255,255,.06)', overflow: 'hidden', mb: '10px' }}>
          <Box sx={{ width: `${(readiness.doneCount / readiness.total) * 100}%`, height: '100%', background: `linear-gradient(90deg,${AT.accent2},${AT.accent})`, transition: 'width .3s' }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {readiness.gates.map((g) => (
            <Box key={g.key} onClick={() => onJump && onJump(g.step)} sx={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', borderRadius: '6px', px: '4px', py: '3px', '&:hover': { background: 'rgba(255,255,255,.03)' } }}>
              {g.done ? <CheckCircle sx={{ fontSize: 15, color: AT.positive }} /> : <RadioButtonUnchecked sx={{ fontSize: 15, color: AT.muted }} />}
              <Typography sx={{ fontSize: '12px', color: g.done ? AT.muted : AT.text, textDecoration: g.done ? 'line-through' : 'none' }}>{g.label}</Typography>
            </Box>
          ))}
        </Box>
      </SRailSection>

      <SRailSection label="SPAWN STRATEGY">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <SRailRow k="Exchange" v={exchangeLabel || '—'} />
          <SRailRow k="Position size" v={size > 0 ? `${size} ${draft.funding_asset || 'USDT'}` : '—'} />
          <SRailRow k="Entry rules" v={String((draft.buy_conditions || []).length)} c={AT.positive} />
          <SRailRow k="Exit rules" v={String((draft.sell_conditions || []).length)} c={AT.negative} />
        </Box>
      </SRailSection>

      <SRailSection label="RISK PROFILE" mb={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '11px', color: AT.positive, background: 'rgba(70,184,138,.12)', border: '1px solid rgba(70,184,138,.32)', px: '8px', py: '3px', borderRadius: '6px' }}>TP {tp || '—'}%</Box>
          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '11px', color: AT.negative, background: 'rgba(217,123,114,.12)', border: '1px solid rgba(217,123,114,.32)', px: '8px', py: '3px', borderRadius: '6px' }}>SL {sl || '—'}%</Box>
          <Box component="span" sx={{ ml: 'auto', fontFamily: AT_MONO, fontSize: '11px', fontWeight: 700, color: band.c }}>{rr ? rr.toFixed(2) : '—'} R:R · {band.label}</Box>
        </Box>
      </SRailSection>
    </Box>
  );
}

function CreateForm({ draft, setDraft, activeStep, setActiveStep, editing, onCancel, onSave, userExchanges }) {
  const patch = (fields) => setDraft(d => ({ ...d, ...fields }));
  const exObj = (userExchanges || []).find((e) => e.id === draft.exchange_id);
  const exchangeLabel = exObj ? (exObj.name || exObj.exchange) : '';
  const readiness = (() => {
    const uniOk = draft.universe_filter?.type === 'list'
      ? (draft.universe_filter?.symbols || []).length > 0
      : !!draft.universe_filter?.type;
    const gates = [
      { key: 'name', label: 'Name your scanner', done: !!draft.name?.trim(), step: 0 },
      { key: 'exchange', label: 'Connect an exchange', done: !!draft.exchange_id, step: 0 },
      { key: 'universe', label: 'Define a universe', done: uniOk, step: 0 },
      { key: 'trigger', label: 'Set a trigger', done: (draft.trigger?.conditions || []).length > 0, step: 1 },
      { key: 'strategy', label: 'Define spawn strategy', done: (draft.buy_conditions || []).length > 0 && (draft.sell_conditions || []).length > 0, step: 2 },
      { key: 'size', label: 'Set position size', done: Number(draft.position_size) > 0, step: 2 },
    ];
    const doneCount = gates.filter((g) => g.done).length;
    return { gates, doneCount, total: gates.length, ready: doneCount === gates.length, firstMissing: gates.find((g) => !g.done) };
  })();

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 332px' }, gap: '16px', alignItems: 'start', mb: 4 }}>
          <Box sx={{ minWidth: 0, bgcolor: AT.card, border: `1px solid ${AT.hairline}`, borderRadius: '14px', boxShadow: '0 1px 2px rgba(0,0,0,.22)', p: { xs: '20px', md: '24px' } }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map(label => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}
        </Stepper>

        {activeStep === 0 && <BasicsStep draft={draft} patch={patch} userExchanges={userExchanges} />}
        {activeStep === 1 && (
          <ScannerTriggerBuilder
            trigger={draft.trigger}
            onTriggerChange={(t) => patch({ trigger: t })}
            cooldownSeconds={draft.cooldown_seconds}
            onCooldownChange={(s) => patch({ cooldown_seconds: s })}
            marketType={draft.market_type}
            interval={draft.primary_interval}
            onIntervalChange={(i) => patch({ primary_interval: i })}
          />
        )}
        {activeStep === 2 && <StrategyStep draft={draft} patch={patch} />}
        {activeStep === 3 && <SafetyStep draft={draft} patch={patch} />}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button onClick={activeStep === 0 ? onCancel : () => setActiveStep(s => s - 1)} variant="outlined" sx={{ textTransform: 'none' }}>
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Box>
            {activeStep < STEPS.length - 1 && (
              <Button
                onClick={() => setActiveStep(s => s + 1)}
                variant="contained"
                disableElevation
                sx={{ ml: 1, color: '#e7eef6', background: 'linear-gradient(180deg,#3d5c7b,#33506b)', textTransform: 'none', fontWeight: 600, '&:hover': { background: 'linear-gradient(180deg,#436686,#395a78)' } }}
              >
                Next
              </Button>
            )}
            {activeStep === STEPS.length - 1 && (
              <>
                <Button
                  onClick={() => onSave(false)}
                  variant="outlined"
                  sx={{ ml: 1, borderColor: 'grey.600', color: 'grey.300', textTransform: 'none', '&:hover': { borderColor: 'grey.500', bgcolor: 'rgba(255,255,255,0.05)' } }}
                >
                  Save (Paused)
                </Button>
                {readiness.ready ? (
                  <Button
                    onClick={() => onSave(true)}
                    variant="contained"
                    disableElevation
                    sx={{ ml: 1, color: '#e7eef6', background: 'linear-gradient(180deg,#3d5c7b,#33506b)', textTransform: 'none', fontWeight: 600, '&:hover': { background: 'linear-gradient(180deg,#436686,#395a78)' } }}
                  >
                    Save &amp; Activate
                  </Button>
                ) : (
                  <Tooltip title={`Next: ${readiness.firstMissing?.label || 'finish the remaining steps'}`} arrow>
                    <Button
                      onClick={() => readiness.firstMissing && setActiveStep(readiness.firstMissing.step)}
                      variant="outlined"
                      sx={{ ml: 1, textTransform: 'none', color: AT.muted, borderColor: 'rgba(255,255,255,.14)' }}
                    >
                      Finish setup to activate
                    </Button>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        </Box>
          </Box>
          <Box sx={{ position: { md: 'sticky' }, top: 16, alignSelf: 'flex-start' }}>
            <ScannerRail draft={draft} exchangeLabel={exchangeLabel} readiness={readiness} onJump={(s) => setActiveStep(s)} />
          </Box>
        </Box>
  );
}


function BasicsStep({ draft, patch, userExchanges }) {
  // Phase B — derive asset class from the selected exchange
  const selectedExchange = useMemo(
    () => (userExchanges || []).find(ex => ex.id === draft.exchange_id) || null,
    [userExchanges, draft.exchange_id]
  );
  const assetClass = getAssetClassFromExchange(selectedExchange);
  const isStockEquity = assetClass === 'stock_equity';

  // Keep draft.asset_class in sync with the selected exchange so handleSave/payload
  // can use it without re-resolving.
  useEffect(() => {
    if (draft.asset_class !== assetClass) {
      patch({ asset_class: assetClass });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetClass]);

  // When switching between crypto and stock, reset the universe_filter shape so
  // we never carry crypto-only fields into a stock scanner (or vice versa).
// When switching between crypto and stock, reset the universe_filter shape so
  // we never carry crypto-only fields into a stock scanner (or vice versa).
  // Also reset funding_asset to a sensible default for the new asset class.
  // When switching between crypto and stock, reset the universe_filter shape so
  // we never carry crypto-only fields into a stock scanner (or vice versa).
  // Also reset funding_asset to a sensible default for the new asset class.
  // Note: universe quote and funding_asset are INDEPENDENT — the spawner converts
  // funding_asset → pair quote at spawn time. Don't link them in the UI.
  useEffect(() => {
    const u = draft.universe_filter || {};
    const supported = getFundingAssetsForClass(isStockEquity ? 'stock_equity' : 'crypto');
    const newFundingAsset = supported.includes(draft.funding_asset)
      ? draft.funding_asset
      : defaultFundingAssetForClass(isStockEquity ? 'stock_equity' : 'crypto');

    if (isStockEquity && u.type !== 'index' && u.type !== 'custom') {
      patch({
        universe_filter: { type: 'index', index: 'sp500', symbols: [] },
        funding_asset: newFundingAsset,
      });
    } else if (!isStockEquity && (u.type === 'index' || u.type === 'custom')) {
      patch({
        universe_filter: { type: 'top_volume', limit: 100, quote: 'USDT', min_volume: 0, fee_filter: 'any', blacklist: [] },
        funding_asset: newFundingAsset,
      });
    } else if (newFundingAsset !== draft.funding_asset) {
      patch({ funding_asset: newFundingAsset });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStockEquity]);

  // Keep universe_filter.quote in sync with funding_asset for crypto top_volume universes.
  // This is the "auto-lock" behavior the user opted into — quote always matches funding asset.


  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Scanner Name"
          value={draft.name}
          onChange={e => patch({ name: e.target.value })}
          fullWidth
          placeholder={isStockEquity ? 'e.g. S&P 500 Momentum Scanner' : 'e.g. Top 100 Momentum Scanner'}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Exchange</InputLabel>
          <Select
            value={draft.exchange_id ?? ''}
            onChange={e => patch({ exchange_id: e.target.value || null })}
            label="Exchange"
            // Without this the closed field would render the raw MenuItem row
            // (avatar and all) squashed into the input.
            renderValue={(val) => {
              const ex = (userExchanges || []).find(e => String(e.id) === String(val));
              if (!ex) return 'Select an exchange…';
              return `${exchangeDisplayName(ex)} — ${exchangeSubtitle(ex)}`;
            }}
          >
            <MenuItem value="" disabled>Select an exchange…</MenuItem>
            {/* Same row treatment as the create-bot picker: logo, name,
                Live/Simulated chip and the venue. Was plain
                "name (Simulated)" text, which also rendered as nothing for the
                Alpaca connection because its name column is blank. */}
            {(userExchanges || []).map(ex => (
              <MenuItem key={ex.id} value={ex.id}>
                <ExchangeOptionRow exchange={ex} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Phase B — show asset class derived from exchange */}
        {draft.exchange_id && (
          <Box sx={{ mt: 1 }}>
            <AssetClassChip assetClass={assetClass} />
          </Box>
        )}
      </Grid>

      {/* Funding Asset — the BALANCE SOURCE for every spawned bot.
          Independent from the pair universe: spawner will convert funding asset
          → pair's quote asset at spawn time (e.g. fund=USDC, pair=SOL/USDT
          triggers USDC→USDT swap then opens SOL position).
          For Alpaca, USD is locked. */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth disabled={!draft.exchange_id || isStockEquity}>
          <InputLabel>Funding Asset</InputLabel>
          <Select
            value={draft.funding_asset || defaultFundingAssetForClass(isStockEquity ? 'stock_equity' : 'crypto')}
            onChange={e => patch({ funding_asset: e.target.value })}
            label="Funding Asset"
          >
            {getFundingAssetsForClass(isStockEquity ? 'stock_equity' : 'crypto').map(a => (
              <MenuItem key={a} value={a}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body2" fontWeight={600}>{a}</Typography>
                  {/* TODO: replace with live balance once exchangeApiService.getBalances exists. */}
                  <Typography variant="caption" color="text.secondary">—</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
<Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
          {isStockEquity
            ? 'Alpaca accounts are USD-funded. Cap and bot capital are measured in USD.'
            : `Spawned bots draw from your ${draft.funding_asset || 'USDT'} balance. If the triggered pair quotes in a different asset, the spawner auto-converts at spawn time.`}
        </Typography>
      </Grid>

      {/* Funding asset / universe mismatch warning.
          When the funding asset isn't the natural quote for pairs in the universe,
          every spawn pays a conversion spread. Stable→stable is usually negligible
          (<1 bp); cross-asset (e.g. BTC funding for USDT pairs) compounds fast. */}
      {!isStockEquity && (() => {
        const mm = describeFundingMismatch(draft.funding_asset, draft.universe_filter);
        if (!mm) return null;
        return (
          <Grid item xs={12}>
            <Alert severity={mm.severity} icon={<WarningAmber fontSize="small" />}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {mm.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {mm.body}
              </Typography>
            </Alert>
          </Grid>
        );
      })()}


      {/* Asset-class-aware market notice */}
      <Grid item xs={12}>
        <Paper
          variant="outlined"
          sx={{
            p: 2, borderRadius: 2,
            display: 'flex', alignItems: 'center', gap: 1.5,
            bgcolor: 'action.hover',
          }}
        >
          {isStockEquity
            ? <ShowChart color="primary" fontSize="small" />
            : <AccountBalance color="primary" fontSize="small" />}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {isStockEquity ? 'US Stock Equity Market' : 'Spot Market'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isStockEquity
                ? 'This scanner watches US-listed equities via Alpaca. Spawned bots trade long-only by default; enable shorts in Strategy step.'
                : 'This scanner watches spot pairs only. Spawned bots trade without leverage.'}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={isStockEquity ? 'Stocks' : 'Spot'}
            color="primary"
            variant="outlined"
          />
        </Paper>
      </Grid>

      {/* Universe picker — swap based on asset class */}
      <Grid item xs={12}>
        {isStockEquity ? (
          <StockUniversePicker
            value={draft.universe_filter}
            onChange={(u) => patch({ universe_filter: u })}
          />
        ) : (
          <UniverseSelector
            universe={draft.universe_filter}
            onUniverseChange={(u) => patch({ universe_filter: u })}
            marketType={draft.market_type}
          />
        )}
      </Grid>
    </Grid>
  );
}


function StrategyStep({ draft, patch }) {
  const isFutures = draft.market_type === 'futures';
  const isStockEquity = draft.asset_class === 'stock_equity';

  // Label for position size field varies by asset class
  const sizeLabel = isFutures
    ? 'Margin Per Bot (USDT)'
    : isStockEquity
      ? 'Quote Per Bot (USD)'
      : 'Quote Per Bot (USDT)';

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Position Sizing</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>per spawned bot</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={isFutures ? 6 : 12}>
              <TextField
                label={sizeLabel}
                type="number"
                value={draft.position_size}
                onChange={e => patch({ position_size: Number(e.target.value) || 0 })}
                fullWidth
                placeholder="e.g. 50"
                inputProps={{ step: 5, min: 0 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}

              />
            </Grid>

            {isFutures && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Leverage: <strong>{draft.leverage}×</strong>
                </Typography>
                <Slider
                  value={draft.leverage}
                  onChange={(_, v) => patch({ leverage: v })}
                  min={1}
                  max={50}
                  step={1}
                  marks={[
                    { value: 1, label: '1×' },
                    { value: 10, label: '10×' },
                    { value: 25, label: '25×' },
                    { value: 50, label: '50×' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={v => `${v}×`}
                  sx={{
                    color: draft.leverage > 20 ? 'error.main'
                      : draft.leverage > 10 ? 'warning.main'
                        : 'primary.main',
                  }}
                />
              </Grid>
            )}
          </Grid>

          {isFutures && (
            <>
              <Divider sx={{ my: 2.5 }} />
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Margin Mode</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Paper
                  variant="outlined"
                  onClick={() => patch({ margin_mode: 'ISOLATED' })}
                  sx={{
                    flex: 1, p: 2, cursor: 'pointer',
                    borderColor: draft.margin_mode === 'ISOLATED' ? 'primary.main' : 'divider',
                    borderWidth: draft.margin_mode === 'ISOLATED' ? 2 : 1,
                    bgcolor: draft.margin_mode === 'ISOLATED' ? 'action.selected' : 'background.paper',
                    '&:hover': { borderColor: 'primary.light' },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>Isolated</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Per-position margin (safer)
                  </Typography>
                </Paper>
                <Paper
                  variant="outlined"
                  onClick={() => patch({ margin_mode: 'CROSSED' })}
                  sx={{
                    flex: 1, p: 2, cursor: 'pointer',
                    borderColor: draft.margin_mode === 'CROSSED' ? 'primary.main' : 'divider',
                    borderWidth: draft.margin_mode === 'CROSSED' ? 2 : 1,
                    bgcolor: draft.margin_mode === 'CROSSED' ? 'action.selected' : 'background.paper',
                    '&:hover': { borderColor: 'primary.light' },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>Cross</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Shared account margin
                  </Typography>
                </Paper>
              </Box>
            </>
          )}
        </Paper>
      </Grid>

      {/* Phase B — Allow Shorts toggle for stock_equity scanners */}
      {isStockEquity && (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              onClick={() => patch({ allow_shorts: !draft.allow_shorts })}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            >
              <Chip
                label={draft.allow_shorts ? 'ON' : 'OFF'}
                size="small"
                color={draft.allow_shorts ? 'info' : 'default'}
                variant={draft.allow_shorts ? 'filled' : 'outlined'}
              />
              <Box>
                <Typography variant="body2" fontWeight={500}>Allow Shorts</Typography>
                <Typography variant="caption" color="text.secondary">
                  Enable short-side conditions. Spawned bots will only short symbols flagged ETB/HTB by Alpaca.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      )}

      <Grid item xs={12}>
        <StrategyBuilder
          buyConditions={draft.buy_conditions}
          sellConditions={draft.sell_conditions}
          onBuyConditionsChange={(c) => patch({ buy_conditions: c })}
          onSellConditionsChange={(c) => patch({ sell_conditions: c })}
          shortOpenConditions={draft.short_open_conditions}
          shortCloseConditions={draft.short_close_conditions}
          onShortOpenConditionsChange={(c) => patch({ short_open_conditions: c })}
          onShortCloseConditionsChange={(c) => patch({ short_close_conditions: c })}
          marketType={draft.market_type}
          positionMode={
            isStockEquity
              ? (draft.allow_shorts ? 'hedge' : 'one_way')
              : draft.position_mode
          }
          assetClass={draft.asset_class}
          profitStop={draft.profit_stop_pct}
          lossStop={draft.loss_stop_pct}
          onProfitStopChange={(v) => patch({ profit_stop_pct: v })}
          onLossStopChange={(v) => patch({ loss_stop_pct: v })}
          interval={draft.primary_interval}
        />
      </Grid>

      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Additional Controls</Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Trailing Stop (%)"
                type="number"
                value={draft.trailing_stop_pct ?? ''}
                onChange={e => patch({ trailing_stop_pct: e.target.value ? Number(e.target.value) : null })}
                fullWidth
                placeholder="None"
                inputProps={{ step: 0.1, min: 0 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Bot Lifetime (minutes)"
                type="number"
                value={draft.max_lifetime_minutes ?? ''}
                onChange={e => patch({ max_lifetime_minutes: e.target.value ? Number(e.target.value) : null })}
                fullWidth
                placeholder="No limit"
                inputProps={{ step: 5, min: 0 }}
              />
            </Grid>
            {/* Per-position exits, inherited by every spawned bot. Distinct
                from Profit/Loss Stop above (cumulative bot pauses) and from
                Max Bot Lifetime (bot-scoped wall clock, not position-scoped). */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Stop Loss (% per trade)"
                type="number"
                value={draft.risk_stop_loss_pct ?? ''}
                onChange={e => patch({ risk_stop_loss_pct: e.target.value ? Number(e.target.value) : null })}
                fullWidth placeholder="None"
                inputProps={{ step: 0.1, min: 0 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Take Profit (% per trade)"
                type="number"
                value={draft.risk_take_profit_pct ?? ''}
                onChange={e => patch({ risk_take_profit_pct: e.target.value ? Number(e.target.value) : null })}
                fullWidth placeholder="None"
                inputProps={{ step: 0.1, min: 0 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Bars in Trade"
                type="number"
                value={draft.risk_max_bars ?? ''}
                onChange={e => patch({ risk_max_bars: e.target.value ? Number(e.target.value) : null })}
                fullWidth placeholder="No limit"
                helperText={`Exit a position after this many ${draft.primary_interval || ''} bars`}
                inputProps={{ step: 1, min: 0 }}
              />
            </Grid>
            {draft.asset_class === 'stock_equity' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Flatten Before Close (min)"
                  type="number"
                  value={draft.risk_flatten_min ?? ''}
                  onChange={e => patch({ risk_flatten_min: e.target.value ? Number(e.target.value) : null })}
                  fullWidth placeholder="Hold through"
                  inputProps={{ step: 1, min: 0 }}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}


function SafetyStep({ draft, patch }) {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Safety Limits</Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Max Concurrent Bots: <strong>{draft.max_concurrent_spawned}</strong>
            </Typography>
            <Slider
              value={draft.max_concurrent_spawned}
              onChange={(_, v) => patch({ max_concurrent_spawned: v })}
              min={1}
              max={20}
              step={1}
              marks={[
                { value: 1, label: '1' },
                { value: 5, label: '5' },
                { value: 10, label: '10' },
                { value: 20, label: '20' },
              ]}
              valueLabelDisplay="auto"
            />

          </Box>

          <TextField
            label="Total Capital Cap (USDT)"
            type="number"
            required
            value={draft.total_capital_cap ?? ''}
            onChange={e => patch({ total_capital_cap: e.target.value ? Number(e.target.value) : null })}
            fullWidth
            placeholder="e.g. 500"
            inputProps={{ step: 50, min: 0, max: 1000 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            error={draft.total_capital_cap != null && Number(draft.total_capital_cap) > 1000}
            helperText={
              draft.total_capital_cap != null && Number(draft.total_capital_cap) > 1000
                ? 'Beta limit: maximum $1000'
                : 'Sum of position sizing across all spawned bots will not exceed this. Beta limit: $1000.'
            }
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Beta limit:</strong> Total capital cap is currently restricted to $1000 across all spawned bots.
            Your exchange balance will be verified when the scanner is activated.
          </Alert>
        </Paper>
      </Grid>
    </Grid>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   Tiny shared display primitive
   ════════════════════════════════════════════════════════════════════════ */

function KV({ label, value, sub }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
        {value ?? '—'}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   PARSE / FORMAT HELPERS
   ════════════════════════════════════════════════════════════════════════ */

function parseTradeInfo(raw) {
  if (raw == null) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

function extractScannerCfg(profile) {
  return parseTradeInfo(profile.trade_info).scanner || {};
}

function fmtSeconds(s) {
  if (!Number.isFinite(s)) return '—';
  if (s >= 3600) return `${(s / 3600).toFixed(1)}h`;
  if (s >= 60) return `${Math.round(s / 60)}m`;
  return `${s}s`;
}

function fmtMoneyShort(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function summarizeUniverse(u) {
  if (!u) return '—';
  if (u.type === 'top_volume') return `Top ${u.limit || 100} by ${u.quote || 'USDT'} vol`;
  if (u.type === 'list') return `${(u.symbols || []).length} symbols`;
  if (u.type === 'category') return `Category: ${u.category}`;
  // Phase B — stock-equity shapes
  if (u.type === 'index') {
    const labels = { sp500: 'S&P 500', nasdaq100: 'NASDAQ-100', russell1000: 'Russell 1000' };
    const n = (u.symbols || []).length;
    return `${labels[u.index] || u.index} (${n || '…'})`;
  }
  if (u.type === 'custom') return `Custom: ${(u.symbols || []).length} tickers`;
  return u.type;
}

// Returns a structured warning object when the funding asset is unlikely to
// match the natural quote of pairs in the universe — meaning every spawn will
// pay a conversion spread.
//
// Returns null when funding asset is compatible (no warning needed).
// Returns { severity, title, body } when there's a mismatch worth surfacing.
//
// Stablecoin pairs (e.g. funding=USDC, universe quote=USDT) get severity='info'
// because the spread is usually <1 bp. Cross-asset (e.g. funding=BTC for USDT
// pairs) gets severity='warning' because the spread can be 5-20+ bp per trade.
function describeFundingMismatch(fundingAsset, universe) {
  if (!fundingAsset || !universe) return null;
  const f = String(fundingAsset).toUpperCase();

  const STABLES = new Set(['USDT', 'USDC', 'FDUSD', 'BUSD', 'TUSD', 'DAI']);
  const isStable = (a) => STABLES.has(String(a || '').toUpperCase());

  // Heuristic: extract likely quote from a symbol like "BTCUSDT" → "USDT".
  const QUOTES = ['USDT', 'USDC', 'FDUSD', 'BUSD', 'TUSD', 'BTC', 'ETH', 'USD'];
  const quoteFromSymbol = (sym) => {
    const s = String(sym || '').toUpperCase().replace('/', '');
    for (const q of QUOTES.sort((a, b) => b.length - a.length)) {
      if (s.endsWith(q) && s.length > q.length) return q;
    }
    return null;
  };

  // Case 1: top_volume universe — explicit quote field
  if (universe.type === 'top_volume') {
    const q = String(universe.quote || 'USDT').toUpperCase();
    if (q === f) return null;
    const bothStable = isStable(f) && isStable(q);
    return {
      severity: bothStable ? 'info' : 'warning',
      title: bothStable
        ? `Stablecoin conversion: ${f} → ${q} on each spawn`
        : `Cross-asset conversion: ${f} → ${q} on each spawn`,
      body: bothStable
        ? `Your universe scans ${q}-quoted pairs, but bots are funded from ${f}. Stablecoin swaps usually cost less than 0.01% per side, so this is mostly fine — but it does mean two extra trades per bot lifecycle (entry + exit conversion).`
        : `Your universe scans ${q}-quoted pairs, but bots are funded from ${f}. Each spawn will swap ${f} → ${q} (and back on exit), exposing you to spread (~0.05–0.2%) and ${f} price movement. Consider funding directly in ${q} instead.`,
    };
  }

  // Case 2: list universe — inspect symbols
  if (universe.type === 'list' && Array.isArray(universe.symbols) && universe.symbols.length) {
    const quoteCounts = {};
    for (const sym of universe.symbols) {
      const q = quoteFromSymbol(sym);
      if (q) quoteCounts[q] = (quoteCounts[q] || 0) + 1;
    }
    const quotes = Object.keys(quoteCounts);
    if (quotes.length === 0) return null;
    if (quotes.length === 1 && quotes[0] === f) return null;

    const total = universe.symbols.length;
    const matching = quoteCounts[f] || 0;
    if (matching === total) return null;

    const dominantQuote = quotes.sort((a, b) => quoteCounts[b] - quoteCounts[a])[0];
    const bothStable = isStable(f) && isStable(dominantQuote);

    return {
      severity: bothStable ? 'info' : 'warning',
      title: `${total - matching} of ${total} symbols don't quote in ${f}`,
      body: bothStable
        ? `Most pairs in your list quote in ${dominantQuote}. Stablecoin conversion (${f} → ${dominantQuote}) is cheap (~0.01%) but adds trades to each bot lifecycle.`
        : `Most pairs in your list quote in ${dominantQuote}. Funding in ${f} means each spawn pays ${f} → ${dominantQuote} spread (~0.05–0.2%) and is exposed to ${f} price moves. Funding directly in ${dominantQuote} would be cheaper.`,
    };
  }

  // Category / index / custom — can't compute mismatch cleanly, skip warning
  return null;
}

function summarizeTrigger(t) {
  if (!t) return '—';
  if (t._preset?.kind === 'volume_spike') {
    return `Vol ≥${t._preset.multiplier}× over ${t._preset.window_seconds}s`;
  }
  if (t.type === 'expression') return t.expression || `${(t.conditions || []).length} conditions`;
  if (t.type === 'volume_spike') return `Vol ≥${t.config?.multiplier || 3}× over ${t.config?.window_seconds || 60}s`;
  return t.type;
}


/* ════════════════════════════════════════════════════════════════════════
   DRAFT / PAYLOAD — unchanged logic
   ════════════════════════════════════════════════════════════════════════ */

function makeDraft() {
  return {
    name: '', description: '', exchange_id: null,
market_type: 'spot', position_mode: 'one_way',
    // Phase B — asset class (resolved from exchange) + stock-equity short toggle
    asset_class: 'crypto',
    allow_shorts: false,
    // Funding asset pool: every spawned bot draws from this asset.
    // total_capital_cap is denominated in this asset. Universe is filtered to
    // pairs where quote === funding_asset.
    funding_asset: 'USDT',
        universe_filter: {
      type: 'top_volume',
      limit: 100,
      quote: 'USDT',
      min_volume: 0,
      fee_filter: 'any',   // ← add
      blacklist: [],
    },
    trigger: { type: 'expression', conditions: [] },
    primary_interval: '5m', cooldown_seconds: 600,
    buy_conditions: [], sell_conditions: [],
    short_open_conditions: [], short_close_conditions: [],
    profit_stop_pct: '', loss_stop_pct: '',
    trailing_stop_pct: null, max_lifetime_minutes: null,
    // Per-position exits — inherited by every spawned bot via trade_info.risk.
    risk_stop_loss_pct: null, risk_take_profit_pct: null,
    risk_max_bars: null, risk_flatten_min: null,
    position_size: 50, leverage: 5, margin_mode: 'ISOLATED',
    max_concurrent_spawned: 3, total_capital_cap: null,
  };
}

function profileToDraft(profile) {
  const ti = parseTradeInfo(profile.trade_info);
  const scannerCfg = ti.scanner || {};
  const futures = ti.futures || {};
  const original = scannerCfg._original_conditions || {};

  const isStockEquity = (ti.asset_class || 'crypto') === 'stock_equity';

  return {
    name: profile.name || '',
    description: scannerCfg.description || '',
    exchange_id: profile.exchange_id ?? null,
    market_type: ti.marketType || 'spot',
    position_mode: futures.positionSide === 'hedge' ? 'hedge' : 'one_way',
    // Phase B
asset_class: ti.asset_class || 'crypto',
    allow_shorts: isStockEquity && !!(ti.short_open_logic || profile.short_open_logic),
    funding_asset: ti.funding_asset
      || profile.funding_asset
      || scannerCfg.universe?.quote
      || defaultFundingAssetForClass(ti.asset_class || 'crypto'),
    universe_filter: scannerCfg.universe || (
      isStockEquity
        ? { type: 'index', index: 'sp500', symbols: [] }
        : { type: 'top_volume', limit: 100, quote: 'USDT' }
    ),
    trigger: reconstructTriggerFromBackend(scannerCfg.trigger),
    primary_interval: scannerCfg.primary_interval || ti.timeframe || '5m',
    cooldown_seconds: scannerCfg.cooldown_seconds ?? 600,
    buy_conditions: original.buy_conditions || [],
    sell_conditions: original.sell_conditions || [],
    short_open_conditions: original.short_open_conditions || [],
    short_close_conditions: original.short_close_conditions || [],
    profit_stop_pct: stripPct(ti.profitStop),
    loss_stop_pct: stripPct(ti.lossStop),
    trailing_stop_pct: ti.trailingStop ? Number(ti.trailingStop) : null,
    risk_stop_loss_pct: ti.risk?.stopLossPct ?? null,
    risk_take_profit_pct: ti.risk?.takeProfitPct ?? null,
    risk_max_bars: ti.risk?.maxBarsInTrade ?? null,
    risk_flatten_min: ti.risk?.flattenBeforeCloseMin ?? null,
    max_lifetime_minutes: scannerCfg.max_lifetime_seconds ? Math.floor(scannerCfg.max_lifetime_seconds / 60) : null,
    position_size: ti.totalInvestment ?? ti.value ?? 50,
    leverage: futures.leverage ?? 5,
    margin_mode: futures.marginType ?? 'ISOLATED',
    max_concurrent_spawned: scannerCfg.max_concurrent_spawned ?? 3,
    total_capital_cap: scannerCfg.total_capital_cap ?? null,
  };
}

function reconstructTriggerFromBackend(savedTrigger) {
  if (!savedTrigger) return { type: 'expression', conditions: [] };
  if (savedTrigger._preset?.kind === 'volume_spike') {
    return {
      type: 'volume_spike',
      config: {
        window_seconds: savedTrigger._preset.window_seconds,
        baseline_seconds: savedTrigger._preset.baseline_seconds,
        multiplier: savedTrigger._preset.multiplier,
        min_recent_volume: savedTrigger._preset.min_recent_volume,
      },
    };
  }
  if (savedTrigger.type === 'expression') {
    return { type: 'expression', conditions: Array.isArray(savedTrigger.conditions) ? savedTrigger.conditions : [] };
  }
  if (savedTrigger.type === 'volume_spike') return savedTrigger;
  return { type: 'expression', conditions: [] };
}

function stripPct(v) {
  if (v == null) return '';
  if (typeof v === 'number') return v;
  return String(v).replace(/[^\d.]/g, '');
}

function draftToPayload(d, enabled) {
  const isFutures = d.market_type === 'futures';
  const isHedge = isFutures && d.position_mode === 'hedge';
  // Phase B — stock equity short mode (mirrors futures hedge in condition shape)
  const isStockEquity = d.asset_class === 'stock_equity';
  const isStockHedge = isStockEquity && !!d.allow_shorts;

  // Translate UI's loose 'crypto' / 'stock_equity' enum to the canonical
  // model values: 'crypto_spot' | 'crypto_futures' | 'stock_equity'.
  // The model's isIn validator rejects anything else.
  const canonicalAssetClass = isStockEquity
    ? 'stock_equity'
    : (isFutures ? 'crypto_futures' : 'crypto_spot');

  const trigger = translateTriggerForBackend(d.trigger);
  const buy_logic = buildLogicFromConditions(d.buy_conditions);
  const sell_logic = buildLogicFromConditions(d.sell_conditions);
  const shortOpenLogic = (isHedge || isStockHedge) ? buildLogicFromConditions(d.short_open_conditions) : null;
  const shortCloseLogic = (isHedge || isStockHedge) ? buildLogicFromConditions(d.short_close_conditions) : null;

const trade_info = {
    timeframe: d.primary_interval, symbol: null, marketType: d.market_type,
    // Phase B — surface asset class so backend trader/spawner can route correctly
    asset_class: canonicalAssetClass,
    // Funding asset — drives pre-flight balance check, universe filter, P&L denom.
    funding_asset: d.funding_asset || defaultFundingAssetForClass(d.asset_class || 'crypto'),
    totalInvestment: d.position_size,
    profitStop: d.profit_stop_pct ? `${d.profit_stop_pct}%` : null,
    lossStop: d.loss_stop_pct ? `${d.loss_stop_pct}%` : null,
    trailingStop: d.trailing_stop_pct ?? null,
    // Per-position exits at the TOP level of trade_info — a sibling of
    // `scanner`, deliberately: the spawner deep-clones trade_info and strips
    // only the scanner block, so this is inherited by every spawned bot for
    // free. Anything placed inside `scanner` would be deleted at spawn.
    ...(() => {
      const n = (v) => { const x = Number(v); return Number.isFinite(x) && x > 0 ? x : null; };
      const risk = {
        ...(n(d.risk_stop_loss_pct) ? { stopLossPct: n(d.risk_stop_loss_pct) } : {}),
        ...(n(d.risk_take_profit_pct) ? { takeProfitPct: n(d.risk_take_profit_pct) } : {}),
        ...(n(d.trailing_stop_pct) ? { trailingStopPct: n(d.trailing_stop_pct) } : {}),
        ...(n(d.risk_max_bars) ? { maxBarsInTrade: Math.floor(n(d.risk_max_bars)) } : {}),
        ...(d.asset_class === 'stock_equity' && n(d.risk_flatten_min)
          ? { flattenBeforeCloseMin: Math.floor(n(d.risk_flatten_min)) } : {}),
      };
      return Object.keys(risk).length ? { risk } : {};
    })(),
    indicators: extractIndicatorsFromConditions([
      ...d.buy_conditions, ...d.sell_conditions,
      ...((isHedge || isStockHedge) ? d.short_open_conditions : []),
      ...((isHedge || isStockHedge) ? d.short_close_conditions : []),
    ]),
    orderSizing: { mode: 'quote', value: d.position_size, assetType: 'quote' },
    futures: isFutures ? {
      type: 'usdt_m', leverage: d.leverage, marginType: d.margin_mode,
      positionSide: d.position_mode,
      ...(isHedge && { shortOpenLogic, shortCloseLogic }),
    } : null,
    // Phase B — stock-equity hedge fields live at the top of trade_info (not under futures)
    ...(isStockHedge && {
      short_open_logic: shortOpenLogic,
      short_close_logic: shortCloseLogic,
    }),
    scanner: {
      trigger, universe: d.universe_filter,
      primary_interval: d.primary_interval, cooldown_seconds: d.cooldown_seconds,
      max_concurrent_spawned: d.max_concurrent_spawned,
      total_capital_cap: d.total_capital_cap,
      max_lifetime_seconds: d.max_lifetime_minutes ? d.max_lifetime_minutes * 60 : null,
      description: d.description,
      _original_conditions: {
        buy_conditions: d.buy_conditions, sell_conditions: d.sell_conditions,
        // Both short modes round-trip — isHedge-only here silently dropped a
        // stock scanner's short rows on every save→reload cycle.
        short_open_conditions: (isHedge || isStockHedge) ? d.short_open_conditions : [],
        short_close_conditions: (isHedge || isStockHedge) ? d.short_close_conditions : [],
      },
      paused: !enabled,
    },
  };

return {
    name: d.name, exchange_id: d.exchange_id,
    buy_logic, sell_logic,
    // Both short modes write the top-level fields (backtests & trader resolve
    // them); the isStockHedge-only gate left futures-hedge scanners without
    // them while the bot wizard wrote both — an asymmetry for no reason.
    ...((isHedge || isStockHedge) && {
      short_open_logic: shortOpenLogic,
      short_close_logic: shortCloseLogic,
    }),
    // Mirror funding_asset to top-level so backend can index/filter without parsing JSON.
// Mirror funding_asset and asset_class to top-level so backend can index/filter
    // without parsing JSON. asset_class also satisfies the model's isIn validator.
    funding_asset: d.funding_asset || defaultFundingAssetForClass(d.asset_class || 'crypto'),
    asset_class: canonicalAssetClass,
    trade_info, status: 'scanner_profile',
  };

}

function translateTriggerForBackend(rawTrigger) {
  const t = rawTrigger || {};
  if (t.type === 'expression' || !t.type) {
    const conditions = Array.isArray(t.conditions) ? t.conditions : [];
    const expression = buildLogicFromConditions(conditions);
    if (!expression || !expression.trim()) throw new Error('Expression trigger has no conditions (Step 2)');
    return { type: 'expression', expression, indicators: extractIndicatorsFromConditions(conditions), conditions };
  }
  if (t.type === 'volume_spike') {
    const cfg = t.config || {};
    const win = Number(cfg.window_seconds) > 0 ? Number(cfg.window_seconds) : 60;
    const base = Number(cfg.baseline_seconds) > 0 ? Number(cfg.baseline_seconds) : 1800;
    const mult = Number(cfg.multiplier) > 0 ? Number(cfg.multiplier) : 3;
    const minVol = Number(cfg.min_recent_volume) > 0 ? Number(cfg.min_recent_volume) : 0;
    let expression = `tickVolumeSpikeRatio(${win},${base}) >= ${mult}`;
    const indicators = [{ name: 'tickVolumeSpikeRatio', windowSec: win, baselineSec: base }];
    if (minVol > 0) {
      expression += ` AND tickVolumeSum(${win}) >= ${minVol}`;
      indicators.push({ name: 'tickVolumeSum', windowSec: win });
    }
    return {
      type: 'expression', expression, indicators, conditions: [],
      _preset: { kind: 'volume_spike', window_seconds: win, baseline_seconds: base, multiplier: mult, min_recent_volume: minVol },
    };
  }
  if (t.type === 'liquidation_cascade') throw new Error('Liquidation cascade trigger is not yet supported');
  throw new Error(`Unknown trigger type: ${t.type}`);
}

function validateDraft(d) {
  const errors = [];
if (!d.name?.trim()) errors.push('Scanner name is required');
  if (!d.exchange_id) errors.push('Please select an exchange (Step 1)');
  if (!d.funding_asset?.trim()) errors.push('Funding asset is required (Step 1)');

  const t = d.trigger || {};
  if (t.type === 'expression' || !t.type) {
    if (!Array.isArray(t.conditions) || t.conditions.length === 0) errors.push('Add at least one trigger condition (Step 2)');
  } else if (t.type === 'volume_spike') {
    const cfg = t.config || {};
    if (!(Number(cfg.window_seconds) > 0)) errors.push('Volume Spike: window must be positive (Step 2)');
    if (!(Number(cfg.baseline_seconds) > 0)) errors.push('Volume Spike: baseline must be positive (Step 2)');
    if (!(Number(cfg.multiplier) > 0)) errors.push('Volume Spike: multiplier must be positive (Step 2)');
  } else if (t.type === 'liquidation_cascade') {
    errors.push('Liquidation Cascade triggers are not yet supported (Step 2)');
  }

  if (d.universe_filter?.type === 'list' && (!d.universe_filter.symbols || d.universe_filter.symbols.length === 0)) {
    errors.push('Add at least one symbol to the universe (Step 1)');
  }
  // Phase B — stock_equity universes must resolve to >= 1 symbol (index fetches async, custom is parsed)
  if (d.asset_class === 'stock_equity'
      && (d.universe_filter?.type === 'index' || d.universe_filter?.type === 'custom')
      && (!d.universe_filter.symbols || d.universe_filter.symbols.length === 0)) {
    errors.push(
      d.universe_filter?.type === 'index'
        ? 'Index universe still loading — wait a moment and try again (Step 1)'
        : 'Add at least one ticker to the custom list (Step 1)'
    );
  }

  const isHedge = d.market_type === 'futures' && d.position_mode === 'hedge';
  // Phase B — stock-equity allow_shorts behaves like hedge for validation
  const isStockHedge = d.asset_class === 'stock_equity' && !!d.allow_shorts;
  const longShortMode = isHedge || isStockHedge;

  if (!d.buy_conditions?.length) errors.push(longShortMode ? 'Add at least one Open Long condition (Step 3)' : 'Add at least one buy/entry condition (Step 3)');
  if (!d.sell_conditions?.length) errors.push(longShortMode ? 'Add at least one Close Long condition (Step 3)' : 'Add at least one sell/exit condition (Step 3)');
  if (longShortMode && !d.short_open_conditions?.length) errors.push('Add at least one Open Short condition (Step 3)');
  if (longShortMode && !d.short_close_conditions?.length) errors.push('Add at least one Close Short condition (Step 3)');
  if (!d.position_size || Number(d.position_size) <= 0) errors.push('Position size must be greater than 0 (Step 3)');
  // Beta cap: total capital across all spawned bots
  const BETA_CAPITAL_CAP = 1000;
  const cap = Number(d.total_capital_cap);
  if (!cap || cap <= 0) {
    errors.push(`During beta, Total Capital Cap is required and must be ≤ $${BETA_CAPITAL_CAP} (Step 4)`);
  } else if (cap > BETA_CAPITAL_CAP) {
    errors.push(`During beta, Total Capital Cap cannot exceed $${BETA_CAPITAL_CAP} (Step 4)`);
  }

  return errors;
}